import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import { AuthProvider } from "./context/AuthContext";
import { PlausibleLoader, PageviewTracker } from "./lib/analytics";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import "./App.css";

// Code-split everything except landing + login + signup (the public hot-path)
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Builder = lazy(() => import("./pages/Builder"));
const TemplatesPage = lazy(() => import("./pages/TemplatesPage"));
const CoverLettersList = lazy(() => import("./pages/CoverLetters").then((m) => ({ default: m.CoverLettersList })));
const CoverLetterEditor = lazy(() => import("./pages/CoverLetters").then((m) => ({ default: m.CoverLetterEditor })));
const PublicCv = lazy(() => import("./pages/PublicCv"));
const Pricing = lazy(() => import("./pages/Pricing"));
const FaqPage = lazy(() => import("./pages/FaqPage"));
const PaymentSuccess = lazy(() => import("./pages/PaymentSuccess"));
const ForgotPassword = lazy(() => import("./pages/PasswordReset").then((m) => ({ default: m.ForgotPassword })));
const ResetPassword = lazy(() => import("./pages/PasswordReset").then((m) => ({ default: m.ResetPassword })));
const Admin = lazy(() => import("./pages/Admin"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const PartnerLanding = lazy(() => import("./pages/PartnerLanding"));
const AtsResumePage = lazy(() => import("./pages/SeoLanding").then((m) => ({ default: m.AtsResumePage })));
const UkCvBuilderPage = lazy(() => import("./pages/SeoLanding").then((m) => ({ default: m.UkCvBuilderPage })));
const AiResumeBuilderPage = lazy(() => import("./pages/SeoLanding").then((m) => ({ default: m.AiResumeBuilderPage })));
const PayslipsList = lazy(() => import("./pages/PayslipsList"));
const PayslipEditor = lazy(() => import("./pages/PayslipEditor"));

const PageLoader = () => (
  <div className="min-h-screen bg-matte-black flex items-center justify-center">
    <div className="w-6 h-6 border-2 border-warm-ivory/15 border-t-burnished-gold rounded-full animate-spin" />
  </div>
);

function App() {
  return (
    <div className="App">
      <HelmetProvider>
        <PlausibleLoader />
        <AuthProvider>
          <BrowserRouter>
            <PageviewTracker />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/auth/callback" element={<AuthCallback />} />
                <Route path="/partner/:apiKey" element={<PartnerLanding />} />
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/builder/:id" element={<Builder />} />
                <Route path="/templates" element={<TemplatesPage />} />
                <Route path="/cover-letters" element={<CoverLettersList />} />
                <Route path="/cover-letter/:id" element={<CoverLetterEditor />} />
                <Route path="/payslips" element={<PayslipsList />} />
                <Route path="/payslip/:id" element={<PayslipEditor />} />
                <Route path="/cv/:slug" element={<PublicCv />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/faq" element={<FaqPage />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/admin" element={<Admin />} />
                {/* SEO landing pages */}
                <Route path="/templates/ats-resume" element={<AtsResumePage />} />
                <Route path="/uk-cv-builder" element={<UkCvBuilderPage />} />
                <Route path="/ai-resume-builder" element={<AiResumeBuilderPage />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </HelmetProvider>
    </div>
  );
}

export default App;
